import type { Map as MapLibreMap, Marker as MapLibreMarker } from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import React, { isValidElement, useEffect, useRef, useState } from "react";
import { Root, createRoot } from "react-dom/client";
import { useAppSelector } from "../../../services/hooks/useReduxHooks";
import { LoadingDotBars } from "../NewLoading/LoadingDotBars";
import { MarkerProps } from "./CustomMarker";

interface MapLibreMapProps {
  center?: { lat: number; lng: number };
  zoom?: number;
  height?: string;
  onClick?: (e: { lat: number; lng: number }) => void;
  onChange?: (e: {
    center: { lat: number; lng: number };
    zoom: number;
  }) => void;
  onLoaded?: (map: MapLibreMap) => void;
  onReset?: () => void;
  children?: React.ReactElement<MarkerProps>[];
}

const MapLibreMapComponent: React.FC<MapLibreMapProps> = ({
  center = { lat: 31.2357, lng: 30.0444 },
  zoom = 15,
  height = "200px",
  onClick,
  onChange,
  onLoaded,
  children,
}) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<MapLibreMap | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const markersRef = useRef<{ [key: string]: MapLibreMarker }>({});
  const markerRootsRef = useRef<Root[]>([]);
  const maplibreglRef = useRef<any>(null);
  const restaurantInfo = useAppSelector((state) => state.config.restaurantInfo);

  const clearMarkers = () => {
    Object.values(markersRef.current).forEach((marker) => marker.remove());
    markersRef.current = {};
    const roots = markerRootsRef.current;
    markerRootsRef.current = [];
    // Deferred: React warns when a root is unmounted synchronously from
    // inside another root's commit (this runs in an effect/cleanup).
    setTimeout(() => roots.forEach((root) => root.unmount()), 0);
  };

  useEffect(() => {
    if (map.current) {
      return;
    }
    if (!mapContainer.current) {
      return;
    }

    setIsLoading(true);
    setIsMapLoaded(false);

    import("maplibre-gl").then((maplibregl) => {
      const container = mapContainer.current;
      if (!container) {
        setIsLoading(false);

        return;
      }

      maplibreglRef.current = maplibregl;

      const baseUrl = window.location.origin;
      const styleUrl = `${baseUrl}/map/restaurant/${restaurantInfo?.slug}/bright1.json`;

      if (maplibregl.getRTLTextPluginStatus() !== "loaded") {
        maplibregl.setRTLTextPlugin(
          "https://unpkg.com/@mapbox/mapbox-gl-rtl-text@0.3.0/dist/mapbox-gl-rtl-text.js",
          true // Lazy load the plugin
        );
      }

      map.current = new maplibregl.Map({
        container,
        style: styleUrl,
        center: [center.lng, center.lat],
        zoom,
        minZoom: 8,
        attributionControl: false,
      });

      map.current.on("load", () => {
        setIsMapLoaded(true);
        setIsLoading(false);

        if (onLoaded) {
          onLoaded(map.current!);
        }
      });

      map.current.on("error", (e) => {
        console.error("MapLibre map error:", e);
        setIsLoading(false);
      });

      // Add navigation control
      // map.current.addControl(new maplibregl.NavigationControl());

      // Add custom reset bounds control
      // if (onReset) {
      //   createResetBoundsButton(map.current);
      // }

      // Handle map click
      if (onClick) {
        map.current.on("click", (e) => {
          const { lng, lat } = e.lngLat;
          onClick({ lat, lng });
        });
      }

      // Handle map movement
      if (onChange) {
        map.current.on("moveend", () => {
          if (!map.current) {
            return;
          }
          const center = map.current.getCenter();
          const zoom = map.current.getZoom();
          onChange({
            center: { lat: center.lat, lng: center.lng },
            zoom,
          });
        });
      }

      setIsLoading(false);

      if (onLoaded) {
        onLoaded(map.current);
      }
    });

    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!map.current || !isMapLoaded) {
      return;
    }

    if (
      !center ||
      typeof center.lat !== "number" ||
      typeof center.lng !== "number"
    ) {
      console.warn("Invalid center coordinates:", center);

      return;
    }

    if (map.current && isMapLoaded) {
      map.current.resize();
      map.current.setCenter([center.lng, center.lat]);

      const currentZoom = map.current.getZoom();
      const targetZoom = zoom || Math.max(currentZoom, 16);

      map.current.flyTo({
        center: [center.lng, center.lat],
        zoom: targetZoom,
        duration: 1000,
        essential: true,
      });
    }
  }, [center?.lat, center?.lng, isMapLoaded]);

  useEffect(() => {
    if (!map.current || !maplibreglRef.current) {
      return;
    }

    const setupMarkers = () => {
      clearMarkers();

      React.Children.forEach(children, (child) => {
        if (!isValidElement<MarkerProps>(child)) {
          return;
        }
        const { lat, lng, icon, rotation = 0, anchor } = child.props;

        if (lat && lng && icon) {
          const markerElement = document.createElement("div");

          markerElement.addEventListener("click", () => {
            map.current?.flyTo({
              center: [lng, lat],
            });
          });

          const markerRoot = createRoot(markerElement);
          markerRoot.render(icon);
          markerRootsRef.current.push(markerRoot);

          const marker = new maplibreglRef.current.Marker({
            element: markerElement,
            rotation,
            rotationAlignment: "map",
            anchor: anchor ?? undefined,
            subpixelPositioning: true,
            className: "maplibre_marker",
          })
            .setLngLat([lng, lat])
            .addTo(map.current!);

          markersRef.current[`${lat}-${lng}`] = marker;
        }
      });
    };

    setupMarkers();

    return clearMarkers;
  }, [children]);

  useEffect(() => {
    if (map.current && isMapLoaded) {
      map.current.resize();
    }
  }, [height, isMapLoaded]);

  return (
    <div style={{ position: "relative", width: "100%", height: height }}>
      {isLoading && <LoadingDotBars style={{ position: "absolute" }} />}
      <div
        ref={mapContainer}
        style={{
          position: "absolute",
          top: 0,
          bottom: 0,
          width: "100%",
          height: "100%",
        }}
      />
    </div>
  );
};

export default MapLibreMapComponent;
